import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Download, FolderOpen, Calendar, User, Key, CheckCircle, XCircle, Eye, X, FileText, Image as ImageIcon } from 'lucide-react';

export default function ClientPortal() {
  const { accessKey: urlAccessKey } = useParams();
  const navigate = useNavigate();
  const [accessKey, setAccessKey] = useState(urlAccessKey || '');
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);

  const api = process.env.REACT_APP_API_BASE || 'http://localhost:4000';

  useEffect(() => {
    if (urlAccessKey) {
      fetchProject(urlAccessKey);
    }
  }, [urlAccessKey]);

  const fetchProject = async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${api}/api/client-portal/${key}`);
      if (res.ok) {
        const data = await res.json();
        setProject(data);
        setVerified(true);
      } else {
        setError('Invalid access key. Please check your email and try again.');
        setVerified(false);
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again later.');
      setVerified(false);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = (e) => {
    e.preventDefault();
    if (accessKey.trim()) {
      navigate(`/client-portal/${accessKey.trim()}`);
      fetchProject(accessKey.trim());
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      html: '🌐',
    };
    return iconMap[ext] || '📎';
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'document';
    if (['xls', 'xlsx'].includes(ext)) return 'spreadsheet';
    if (ext === 'html') return 'html';
    return 'other';
  };

  const canViewFile = (filename) => {
    const type = getFileType(filename);
    return ['image', 'pdf', 'document', 'spreadsheet', 'html'].includes(type);
  };

  const handleViewFile = (file) => {
    setViewingFile(file);
  };

  const renderFileViewer = () => {
    if (!viewingFile) return null;

    const fileType = getFileType(viewingFile.originalName);
    const fileUrl = `${api}${viewingFile.path}`;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-purple-600" />
              <div>
                <h3 className="font-semibold text-lg">{viewingFile.originalName}</h3>
                <p className="text-sm text-gray-600">{formatFileSize(viewingFile.size)}</p>
              </div>
            </div>
            <button
              onClick={() => setViewingFile(null)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* File Content */}
          <div className="flex-1 overflow-auto p-6 bg-gray-50">
            {fileType === 'image' && (
              <div className="flex items-center justify-center h-full">
                <img
                  src={fileUrl}
                  alt={viewingFile.originalName}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            )}

            {fileType === 'pdf' && (
              <iframe
                src={fileUrl}
                className="w-full h-full min-h-[600px] rounded-lg shadow-lg"
                title={viewingFile.originalName}
              />
            )}

            {fileType === 'document' && (
              <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
                <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl">
                  <FileText className="w-16 h-16 text-white" />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{viewingFile.originalName}</h3>
                  <p className="text-gray-600 mb-4">Microsoft Word Document</p>
                  <p className="text-sm text-gray-500 max-w-md mb-2">
                    Word documents cannot be previewed directly in the browser.
                    Please download the file to view it.
                  </p>
                  <p className="text-xs text-gray-400">
                    Size: {formatFileSize(viewingFile.size)}
                  </p>
                </div>
                <a
                  href={fileUrl}
                  download
                  className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
                >
                  <Download className="w-6 h-6" />
                  Download Document
                </a>
              </div>
            )}

            {fileType === 'spreadsheet' && (
              <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
                <div className="w-32 h-32 bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center shadow-2xl">
                  <FileText className="w-16 h-16 text-white" />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{viewingFile.originalName}</h3>
                  <p className="text-gray-600 mb-4">Microsoft Excel Spreadsheet</p>
                  <p className="text-sm text-gray-500 max-w-md mb-2">
                    Excel spreadsheets cannot be previewed directly in the browser.
                    Please download the file to view it.
                  </p>
                  <p className="text-xs text-gray-400">
                    Size: {formatFileSize(viewingFile.size)}
                  </p>
                </div>
                <a
                  href={fileUrl}
                  download
                  className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
                >
                  <Download className="w-6 h-6" />
                  Download Spreadsheet
                </a>
              </div>
            )}

            {fileType === 'html' && (
              <iframe
                src={fileUrl}
                className="w-full h-full min-h-[600px] rounded-lg shadow-lg bg-white"
                title={viewingFile.originalName}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              />
            )}
          </div>

          {/* Footer with Download Button (if allowed) */}
          {project?.filePermission === 'download' && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <a
                href={fileUrl}
                download
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
              >
                <Download className="w-5 h-5" />
                Download File
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-16">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block p-4 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl shadow-2xl mb-6">
              <Lock className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Client Portal
            </h1>
            <p className="text-gray-600 text-lg">
              Enter your access key to view your project files
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
            <form onSubmit={handleVerify}>
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <Key className="w-4 h-4" />
                  Access Key
                </label>
                <input
                  type="text"
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                  placeholder="Enter your access key from email"
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all font-mono text-lg"
                  required
                  autoFocus
                />
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Access Project Files'
                )}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-start gap-3 text-sm text-gray-600">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 font-bold text-xs">i</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-700 mb-1">Can't find your access key?</p>
                  <p>Check your email from BuildYourDevOps. The access key was sent when your project files were uploaded.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-16">
      <div className="max-w-6xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-2xl mb-6 animate-pulse">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Welcome, {project?.clientName}!
          </h1>
          <p className="text-gray-600 text-lg">
            {project?.filePermission === 'download'
              ? 'Your project files are ready to view and download'
              : 'Your project files are ready to view'}
          </p>
          {project?.filePermission === 'view' && (
            <div className="mt-4 inline-block px-4 py-2 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
              <p className="text-sm text-yellow-800 font-medium">
                🔒 View Only Mode - Downloads are disabled for this project
              </p>
            </div>
          )}
        </div>

        {/* Project Info Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border border-gray-100">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl">
              <FolderOpen className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">{project?.projectName}</h2>
              <p className="text-gray-600">Project Files & Documents</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-8 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Client</p>
                <p className="font-semibold text-gray-900">{project?.clientName}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Uploaded</p>
                <p className="font-semibold text-gray-900">
                  {project?.createdAt && formatDate(project.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <FolderOpen className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Files</p>
                <p className="font-semibold text-gray-900">{project?.files?.length || 0} files</p>
              </div>
            </div>
          </div>
        </div>

        {/* Files List */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span className="text-3xl">📁</span>
            Project Files
          </h3>

          {project?.files && project.files.length > 0 ? (
            <div className="space-y-3">
              {project.files.map((file, index) => (
                <div
                  key={index}
                  className="group flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white hover:from-purple-50 hover:to-indigo-50 rounded-xl border-2 border-gray-100 hover:border-purple-200 transition-all"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="text-4xl">{getFileIcon(file.originalName)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {file.originalName}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {canViewFile(file.originalName) && (
                      <button
                        onClick={() => handleViewFile(file)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                      >
                        <Eye className="w-5 h-5" />
                        View
                      </button>
                    )}

                    {project.filePermission === 'download' && (
                      <a
                        href={`${api}${file.path}`}
                        download
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                      >
                        <Download className="w-5 h-5" />
                        Download
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-gray-600">No files available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-12">
          <div className="inline-block p-6 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-2xl border-2 border-purple-200">
            <p className="text-gray-700 mb-2">
              Need help or have questions about these files?
            </p>
            <a
              href="https://calendly.com/aneeshthedevops/hello-there-intro-call"
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 font-semibold hover:text-purple-800 transition-colors"
            >
              Schedule a call with us →
            </a>
          </div>
        </div>
      </div>

      {/* File Viewer Modal */}
      {viewingFile && renderFileViewer()}
    </div>
  );
}
